<template>
  <div
    v-if="filters && filters.length > 0"
    v-click-outside="vcoConfig"
    class="w-40 h-full ml-0 mr-2"
  >
    <div
      type="text"
      class="h-full text-sm p-2 border rounded border-solid border-color cursor-pointer bg-white"
      @click="toggleDropdown"
    >
      <div class="h-full flex justify-between items-center">
        <div class="flex-grow">
          {{ $t('components.th_datatable.sale_stream_filter.terminal.button') }}
        </div>
        <el-icon>
          <arrow-up-icon v-if="dropdownOpen" />
          <arrow-down-icon v-else />
        </el-icon>
      </div>
    </div>
    <div
      v-show="dropdownOpen"
      class="absolute z-[2100] mt-[5px] rounded-b border bg-white"
      data-testid="filters-dropdown"
    >
      <div class="absolute -left-px -top-3 h-3 w-40" />

      <div
        v-if="isBusinessUnitsLoading"
        class="flex h-11 w-40 items-center justify-center"
      >
        <th-loading :size="20" />
      </div>

      <div v-else class="flex max-h-[428px] max-w-72 flex-col">
        <div class="overflow-x-hidden overflow-y-auto px-6 py-4">
          <div
            v-for="(businessUnit, index) in businessUnits"
            :key="businessUnit.id || index"
          >
            <business-unit-group
              v-model="streams"
              :business-unit="businessUnit"
              :all-business-units="businessUnits"
              :is-last="index === businessUnits.length - 1"
              @update:modelValue="(streams) => updateSaleStreams(streams)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import compare from 'just-compare'
import debounce from 'debounce'
import { defineAsyncComponent } from 'vue'
import { useLocationStore } from '@shared/store/location'
import ThLoading from '@shared/components/loading'
import { ArrowUpIcon, ArrowDownIcon } from '@shared/assets/icons'
import { storeToRefs } from 'pinia'

export default {
  name: 'SaleStreamsButton',
  components: {
    BusinessUnitGroup: defineAsyncComponent(
      () => import('./business-unit-group')
    ),
    ThLoading,
    ArrowUpIcon,
    ArrowDownIcon
  },
  props: {
    modelValue: {
      type: Object,
      required: false,
      default: () => ({})
    },
    filters: {
      type: Array,
      required: true
    }
  },
  emits: [
    'update:modelValue',
    'filter',
    'submit',
    'toggled-dropdown',
    'close-dropdown'
  ],
  setup() {
    const { currentLocation } = storeToRefs(useLocationStore())
    return {
      currentLocation
    }
  },
  data() {
    return {
      streams: [],
      dropdownOpen: false,
      isBusinessUnitsLoading: true,
      tagsObject: this.normaliseIncomingFilters(),
      vcoConfig: {
        handler: this.closeOnOutsideClick,
        middleware: this.vcoMiddleware
      }
    }
  },
  computed: {
    businessUnits() {
      const salesStreamItem = this.filters.find(
        (item) => item.name === 'salesStream'
      )

      if (!salesStreamItem || !salesStreamItem.resources) {
        return [] // Return an empty array if no salesStream item is found
      }

      const nonEmptyBusinessUnits = salesStreamItem.resources.reduce(
        (acc, businessUnit) => {
          // Filter out salesStreams with null terminalId (ECOM type not needed)
          const filteredStreams = businessUnit.salesStreams.filter(
            (stream) => stream.terminalId !== null
          )

          if (filteredStreams.length > 0) {
            // Sort the salesStreams by terminalId in ascending order
            filteredStreams.sort((a, b) => {
              if (a.terminalId && b.terminalId) {
                return (
                  Number.parseInt(a.terminalId, 10) -
                  Number.parseInt(b.terminalId, 10)
                )
              }
              return 0
            })

            // Add the modified businessUnit to the accumulator
            acc.push({ ...businessUnit, salesStreams: filteredStreams })
          }

          return acc
        },
        []
      )

      return nonEmptyBusinessUnits
    }
  },
  watch: {
    tagsObject: {
      handler: function (newValues) {
        this.$emit('update:modelValue', newValues)
      },
      deep: true
    },
    currentLocation: {
      handler(newLocation, oldLocation) {
        this.handleChangeLocation(newLocation, oldLocation)
      },
      deep: true
    },
    modelValue(newValues, oldValues) {
      this.streams = newValues?.salesStream?.value || []
      if (this.currentLocation) {
        this.handleChangeLocation()
      }
      if (!compare(newValues, oldValues)) {
        this.tagsObject = this.normaliseIncomingFilters()
      }
    },
    businessUnits(newUnits) {
      this.isBusinessUnitsLoading = newUnits.length === 0
    }
  },
  methods: {
    handleChangeLocation(
      newLocation = this.currentLocation,
      oldLocation = this.currentLocation
    ) {
      if (newLocation && newLocation !== oldLocation) {
        this.streams = []
        this.tagsObject = this.normaliseIncomingFilters()
        this.updateSaleStreams([])
        return
      }

      this.tagsObject = this.normaliseIncomingFilters()
      this.updateSaleStreams(this.streams)
    },
    getSalesStreamsByBusiness(businessUnitId) {
      return (
        this.businessUnits
          .find((businessUnit) => businessUnit.id === businessUnitId)
          ?.salesStreams?.map((saleStream) => saleStream.id) || []
      )
    },
    updateSaleStreams(streams) {
      this.streams = streams
      this.tagsObject.salesStream.type = 'multiselect'
      this.tagsObject.salesStream.value = this.streams
      this.tagsObject.salesStream.label = 'Sale Streams'
      this.tagsObject.salesStream.name = 'salesStream'

      this.dispatchFilter()
      return
    },
    normaliseIncomingFilters() {
      return this.filters.reduce(
        (incomingFilters, item) => {
          incomingFilters[item.name] = {
            value: null,
            ...item,
            ...incomingFilters[item.name]
          }
          return incomingFilters
        },
        {
          salesStream: { label: null, name: null, value: null },
          ...this.modelValue
        }
      )
    },
    dispatchFilter() {
      const filter = {}
      Object.entries(this.tagsObject)
        .filter((item) => {
          return !(
            item.value === null ||
            item.value === '' ||
            item[1].value === null ||
            item[1].value === '' ||
            this.isEmptyArray(item[1].value)
          )
        })
        .forEach((item) => {
          filter[item[0]] = item[1].value
        })

      this.$emit('filter', filter, this.tagsObject)

      this.handleSubmit()
    },
    handleSubmit: debounce(function () {
      const trimmedTagsObject = this.prepareTagsObject(this.tagsObject)

      this.$emit('submit', trimmedTagsObject)
      return trimmedTagsObject
    }, 1700),
    prepareTagsObject(tagsObject) {
      const trimmed = { ...tagsObject }

      // remove trailing whitespace in all nested levels
      function trimInputs(obj) {
        for (const key in obj) {
          if (obj[key] !== null && typeof obj[key] === 'object') {
            // works on objects and arrays
            obj[key] = trimInputs(obj[key])
          }

          if (typeof obj[key] === 'string') {
            obj[key] = obj[key].trim()
          }
        }
        return obj
      }

      trimInputs(trimmed)

      return trimmed
    },
    closeDropdown() {
      this.dropdownOpen = false
      this.$emit('close-dropdown')
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen
      if (this.dropdownOpen === false) this.$emit('close-dropdown')
      this.$emit('toggled-dropdown')
    },
    closeOnOutsideClick() {
      if (this.dropdownOpen) {
        this.closeDropdown()
      }
    },
    isEmptyArray(value) {
      return Array.isArray(value) && !value.length
    },
    vcoMiddleware(event) {
      if (event.target?.closest?.('.el-popper')) {
        return false
      }
      return true
    }
  }
}
</script>
